<template>
  <div class="sidebar-wrapper">
    <div class="sidebar d-flex flex-column">
      <div class="sidebar-header flex-shrink-0">
        <router-link to="/" class="eg-top-logo">
          <img
            v-if="isMenuHided"
            src="../../../assets/logo-small.svg"
            alt="Essay Grader"
          />
          <img
            v-if="!isMenuHided"
            src="../../../assets/logo.svg"
            alt="Essay Grader"
          />
        </router-link>
        <transition name="sidebar-maximized-transition">
          <version-badge v-if="!isMenuHided" class="ml-auto" />
        </transition>
      </div>

      <div class="divider"></div>

      <sidebar-main-menu
        ref="mainMenu"
        :menu="mainMenu"
        :is-menu-hided="isMenuHided"
      />

      <div class="divider"></div>

      <div
        ref="workspacesWrapper"
        class="workspaces-wrapper d-flex flex-column"
        data-testid="workspaces-wrapper"
      >
        <div class="classes-assignments paragraph-s">
          {{ $t("common.classesAndAssignments") }}
        </div>
        <sidebar-workspace
          v-for="(_, workspaceKey) in workspaces"
          :key="workspaceKey"
          :ref="workspaceKey"
          :workspace-key="workspaceKey"
        />
      </div>

      <div
        v-if="isMenuHided"
        class="sidebar-menu-item my-3 d-flex justify-content-center"
      >
        <eg-button
          v-b-tooltip.hover.right="
            tipMethod($t('common.classesAndAssignments'))
          "
          type="ordinary"
          icon="folder"
          icon-size="medium"
          icon-color="#566370"
          class="menu-item-button"
          :active="$route?.name.startsWith('workspace')"
          @click="toggleMenu"
        />
      </div>

      <div v-if="isMenuHided" class="divider"></div>

      <transition name="sidebar-maximized-transition">
        <sidebar-banner v-if="!isMenuHided" />
      </transition>

      <eg-b-button
        variant="reset"
        class="toggle-menu-button ml-auto p-0"
        size="sm"
        @click="toggleMenu"
      >
        <eg-b-icon
          :icon="isMenuHided ? 'chevron-right' : 'chevron-left'"
          size="sm"
        ></eg-b-icon>
      </eg-b-button>
    </div>
  </div>
</template>

<script>
import upgradePlanMixin from "@/mixins/upgradePlan";
import trackAndRedirectToUpgrade from "@/mixins/trackAndRedirectToUpgrade";
import SidebarWorkspace from "@/components/global/sidebar/sidebar_workspace.vue";
import SidebarBanner from "@/components/global/sidebar/sidebarBanner/sidebar-banner.vue";
import SidebarMainMenu from "@/components/global/sidebar/sidebar_main_menu.vue";
import VersionBadge from "@/components/global/version_badge.vue";
import { LOCAL_STORAGE_MENU_HIDDEN_KEY, PLATFORMS } from "@/constants";
import { getImgPath } from "@/utils/getCorrectPath";
import { openCannyAuthenticatedLink } from "@/init/canny";
import { openCrispChat } from "@/init/crisp";
import { resetOnboardings } from "@/init/onboarding/onboardings";
import { onboardingContext } from "@/init/onboarding/onboardingContext";
import { mapGetters } from "vuex";

const HELP_CENTER_LINK = "https://help.essaygrader.ai";
const WHATS_NEW = "https://www.essaygrader.ai/feature-updates";
const FEATURE_REQUESTS_LINK = "https://essaygrader.canny.io/feature-requests";

export default {
  name: "GlobalSidebar",

  components: {
    SidebarBanner,
    SidebarWorkspace,
    SidebarMainMenu,
    VersionBadge,
  },

  mixins: [
    /*
    this.upgradeHandler
    */
    upgradePlanMixin,
    trackAndRedirectToUpgrade,
  ],

  inject: ["workspacesProvider", "updateSidebarState"],

  data() {
    return {
      WHATS_NEW,
      HELP_CENTER_LINK,
      isMenuHided:
        localStorage.getItem(LOCAL_STORAGE_MENU_HIDDEN_KEY) === "true",
    };
  },

  computed: {
    ...mapGetters("config", ["features"]),
    ...mapGetters(["isOrgAdmin", "orgId", "clerkUserId"]),
    metricsPath() {
      if (this.isOrgAdmin && this.orgId) {
        return `/metrics/organization/${this.orgId}/overview`;
      }
      return `/metrics/user/${this.clerkUserId || ""}/overview`;
    },
    workspaces() {
      const order = [
        PLATFORMS.ESSAYGRADER,
        PLATFORMS.GOOGLE,
        PLATFORMS.CANVAS,
        PLATFORMS.SCHOOLOGY,
      ];

      return Object.keys(this.workspacesProvider?.workspaces || {}).length > 0
        ? order.reduce((acc, key) => {
            if (this.workspacesProvider?.workspaces[key]) {
              acc[key] = this.workspacesProvider?.workspaces[key];
            }

            return acc;
          }, {})
        : {
            essaygrader: {
              folders: [],
              foldersMap: {},
            },
          };
    },

    mainMenu() {
      return [
        {
          name: this.$t("common.home"),
          to: "/",
          icon: "home-dashboard",
        },
        {
          name: this.$t("common.metrics"),
          icon: "grid-layout",
          children: [
            {
              name: "Class Metrics",
              to: this.metricsPath,
              minimizedIcon: "class-group",
              activeMatcher: (path) => path.startsWith("/metrics"),
            },
            ...(this.isOrgAdmin && this.orgId
              ? [
                  {
                    name: "School Metrics",
                    to: `/organization/${this.orgId}/usage-dashboard`,
                    minimizedIcon: "metrics-screen",
                    activeMatcher: (path) => path.includes("/usage-dashboard"),
                  },
                ]
              : []),
          ],
        },
        {
          name: this.$t("rubrics.rubrics"),
          to: "/rubrics",
          icon: "star",
        },
        {
          name: this.$t("common.teachingTools"),
          icon: "teaching-tools",
          children: [
            {
              name: this.$t("essays.aiDetectorTitle"),
              to: "/ai-detector",
              minimizedIcon: "monitor-search-shield",
            },
            {
              name: this.$t("common.summarizer"),
              to: "/summarize",
              minimizedIcon: "list-paragraph",
            },
          ],
        },
        {
          name: "Help",
          icon: "message-question-help",
          ref: "helpMenu",
          children: [
            {
              name: "Learning Resources",
              ref: "lrMenu",
              class: "learning-resources-submenu",
              icon: "book-open",
              children: [
                {
                  name: "Tutorials",
                  href: HELP_CENTER_LINK,
                  target: "_blank",
                  icon: "notebook",
                },
                {
                  name: "What's New",
                  href: WHATS_NEW,
                  target: "_blank",
                  icon: "message-question-help",
                },
                ...(this.features.showOnboardingTour
                  ? [
                      {
                        name: "Guided Tour",
                        icon: "information",
                        testId: "guided-tour-nav-item",
                        onClick: this.onClickResetOnboardings,
                      },
                    ]
                  : []),
              ],
            },
            {
              name: "Support Channels",
              icon: "help-wheel",
              children: [
                {
                  name: "Chat Support",
                  minimizedIcon: "list-paragraph",
                  icon: "messages-chat",
                  onClick: this.openSupportChat,
                },
                // TODO: uncomment once Canny restored our company account (Chan TBD)
                // {
                //   name: "Request a Feature",
                //   minimizedIcon: "list-paragraph",
                //   icon: "share-arrow-sqaure",
                //   onClick: this.requestFeature,
                // },
              ],
            },
          ],
        },
      ];
    },
  },

  watch: {
    isMenuHided(newValue) {
      this.updateSidebarState(newValue);
    },

    workspaces: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          onboardingContext.workspaceEssaygrader =
            this.$refs.essaygrader?.[0]?.$el;
        });
      },
    },
  },

  created() {
    // Initialize sidebar state in provider
    this.updateSidebarState(this.isMenuHided);
  },

  methods: {
    getImgPath,
    tipMethod(title) {
      return this.isMenuHided ? title : "";
    },

    toggleMenu() {
      this.isMenuHided = !this.isMenuHided;
      localStorage.setItem(LOCAL_STORAGE_MENU_HIDDEN_KEY, this.isMenuHided);
    },
    requestFeature() {
      openCannyAuthenticatedLink(FEATURE_REQUESTS_LINK);
    },
    openSupportChat() {
      openCrispChat();
    },
    async onClickResetOnboardings() {
      await resetOnboardings();
      this.$eventBus.$emit("onboarding:reset");
    },

    expandHelpMenu() {
      this.$refs.mainMenu?.expandHelpMenu();
    },

    collapseHelpMenu() {
      this.$refs.mainMenu?.collapseHelpMenu();
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-wrapper {
  --sidebar-transition-duration: 0.125s;
  width: var(--sidebar-width);
  will-change: width;
  transition: width var(--sidebar-transition-duration);
  height: calc(100vh - var(--maintenance-height));
  position: sticky;
  top: 0;

  .sidebar-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    height: calc(var(--main-header-height) - 1px);
  }
  .eg-top-logo {
    height: 23px;
    img {
      top: -7px;
      position: relative;
    }
  }

  .menu-icon {
    cursor: pointer;
  }
}

.workspaces-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  max-height: 10000px;
  opacity: 1;
  transition: max-height var(--sidebar-transition-duration) ease,
    opacity 0.3s ease var(--sidebar-transition-duration);

  .is-sidebar-minimized & {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height var(--sidebar-transition-duration) ease,
      opacity 0.1s ease;
  }
  & .classes-assignments {
    position: absolute;
    right: 15px;
    left: 0;
    padding: 16px 16px 0 16px;
    font-weight: var(--typography-font-weight-fw-600);
    line-height: 150%;
    background: white;
    z-index: 1;
  }
  // Needed to increase onboarding space, to cover correct area
  ::v-deep .workspace-wrapper.eg-tree:nth-child(2) {
    padding-top: 3rem;
  }
}

.sidebar {
  width: 100%;
  height: 100%;
}

.divider {
  border-bottom: 1px solid hsl(207, 27%, 92%);
}

.toggle-menu-button {
  background-color: var(--color-surface-primary-default);
  color: white;
  border-radius: 100px;
  display: block;
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(50%, -50%);
  padding: 2.25px 5px 2.25px 3px !important;

  &:hover {
    opacity: 0.85;
    color: white;
  }
}
</style>

<style lang="scss">
.folders-provider:not(.is-sidebar-minimized) .learning-resources-submenu {
  gap: 0;
  .icon-left {
    margin-right: 8px;
  }
}
.is-sidebar-minimized {
  .toggle-menu-button {
    padding: 2.25px 3px 2.25px 5px !important;
  }
}

.sidebar-maximized-transition-enter-active {
  transition: all 0.3s;
  transition-delay: var(--sidebar-transition-duration);
}

.sidebar-maximized-transition-enter,
.sidebar-maximized-transition-leave-to {
  opacity: 0;
}
</style>
